import {
  Component,
  ChangeDetectionStrategy,
  inject,
  effect,
  ElementRef,
  viewChild,
} from '@angular/core';
import { CostMeterService } from '../cost-meter.service';

/**
 * CostMeterComponent — display-only sidebar widget.
 *
 * Shows the mocked AI cost figure and status label. All state is sourced
 * from CostMeterService (root-scoped). No inputs, no outputs, no user controls.
 * Rendered unconditionally in SidebarComponent's footer area.
 *
 * Count-up animation: writes directly to the #costDisplay element's textContent
 * via ElementRef inside a plain RAF loop — no signal writes, no effect re-runs,
 * no Angular change detection churn during the animation.
 */
@Component({
  selector: 'app-cost-meter',
  standalone: true,
  imports: [],
  templateUrl: './cost-meter.component.html',
  styleUrl: './cost-meter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CostMeterComponent {
  protected readonly costMeterService = inject(CostMeterService);

  // Template reference to the cost <dd> element — written to directly by the RAF loop
  private readonly costDisplay = viewChild<ElementRef<HTMLElement>>('costDisplay');

  // Track the RAF handle so we can cancel a previous animation if a new nav fires
  // before the previous 300 ms window has ended
  private _rafId: number | null = null;

  constructor() {
    // React only to cost changes. No signal writes here — DOM is updated directly.
    effect(() => {
      const targetCost = this.costMeterService.currentCost();  // reactive dependency
      const targetFormatted = this.costMeterService.formattedCost();

      const el = this.costDisplay()?.nativeElement;
      if (!el) {
        return;
      }

      // Cancel any in-progress animation from a previous navigation
      if (this._rafId !== null) {
        cancelAnimationFrame(this._rafId);
        this._rafId = null;
      }

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        el.textContent = targetFormatted;
        return;
      }

      // Parse the currently displayed value as the animation start point
      const startValue = parseFloat(el.textContent?.replace('$', '') ?? '') || targetCost;
      const endValue = targetCost;
      const duration = 300;
      let startTime: number | null = null;

      const animate = (timestamp: number) => {
        if (startTime === null) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);

        el.textContent = `$${(startValue + (endValue - startValue) * progress).toFixed(2)}`;

        if (progress < 1) {
          this._rafId = requestAnimationFrame(animate);
        } else {
          this._rafId = null;
        }
      };

      this._rafId = requestAnimationFrame(animate);
    });
  }
}
